import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { RecipientsService } from 'app/main/pages/contacts/recipients/recipients.service';
import { Organization, Recipient, RecipientRole, Personfield } from '../../edit-template/edit-template.dto';
import { Activity } from './recipient-modal.dto';
import { setRecipientData, setOrgnizationData } from 'app/global/get-recipient-data';
import { debounceTime, delay, distinctUntilChanged, Observable, of, Subscription, take } from 'rxjs';
import { HelperService } from '../../edit-template/helper.service';
import { TranslocoService } from '@jsverse/transloco';
import { organizationFields } from 'app/global/recipientfields';
import { RecipientfieldsService } from '../../settings/recipientfields/recipientfields.service';
import { searchOrganizationCustomFilter } from 'app/global/auto-complete';
import { OrganizationService } from '../../contacts/organizations/organization.service';
import { IOrganizationDTO } from '../../contacts/organizations/organizations.dto';
import { MetaDataSectionEnum } from '../../settings/documentfields/meta-data.helper';
import { ParseNamePipe } from 'app/pipes/parse-name.pipe';

@Component({
  selector: 'app-recipient-modal',
  templateUrl: './recipient-modal.component.html',
  styleUrls: ['./recipient-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RecipientModalComponent implements OnInit ,OnDestroy {
  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('personinput') personinput!: QueryList<ElementRef>;
  @Input()
  set _recipientRole(value: RecipientRole) {
    this.recipientRole = value;
  }
  get _recipientRole() {
    return this.recipientRole;
  }
  @Input()
  set _updateRecipient(value: boolean) {
    this.updateRecipient = value;
  }
  get _updateRecipient() {
    return this.updateRecipient;
  }
  @Input()
  set _navigation(value: string) {
    this.navigation = value || 'recipient';
  }
  get _navigation() {
    return this.navigation;
  }
  @Input()
  set _activeIndex(value: number) {
    this.activeIndex = value || 0;
  }
  get _activeIndex() {
    return this.activeIndex;
  }
  @Input()
  get _isSendForSignature(): boolean {
    return this.isSendForSignature;
  }
  set _isSendForSignature(value: boolean) {
    this.isSendForSignature = value || false;
  }
  public isSendForSignature: boolean = false;
  @Input()
  get isEditParticipientFromSigningPage(): boolean {
    return this.editParticipientFromSigningPage;
  }
  set isEditParticipientFromSigningPage(value: boolean) {
    this.editParticipientFromSigningPage = value || false;
  }
  public editParticipientFromSigningPage: boolean = false;
  public hiddenFields: boolean = false;
  public changeText: string = this._translocoService.translate("Show more (general) - Show more");
  public contactForm: FormGroup;
  public roleForm: FormGroup;
  public activity: Activity = { loading: false, submitted: false, phone_no_valid: true, autocompleteloader: false };
  public keyword: string = 'name';
  public recipientRole: RecipientRole
  public updateRecipient: boolean = false;
  public organizationSearch = [];
  public autoCompleteLoaders = { recloader: false, orgloader: false };
  public timeOutSubscription: Subscription;
  public navigation: string;
  public activeIndex: number;
  private timeoutId?: ReturnType<typeof setTimeout>;
  public metaDataSectionEnum = MetaDataSectionEnum;
  public contactAttachments = {
    personfields: {},
    orgfields: {}
  };

  /**
   * Contructor
   * @param formBuilder 
   * @param recipientsService 
   * @param globalService 
   * @param activeModal 
   */
  constructor(
    private formBuilder: FormBuilder,
    private recipientsService: RecipientsService,
    private organizationService: OrganizationService,
    private globalService: GlobalService,
    public activeModal: NgbActiveModal,
    private helperService: HelperService,
    private _translocoService: TranslocoService,
    private recipientfieldsService: RecipientfieldsService,
    private parseName: ParseNamePipe
  ) { }

  ngOnInit(): void {
    this.initForm(this.recipientRole);
  }

  initForm(recipientRole: RecipientRole) {
    this.roleForm = this.formBuilder.group({
      id: [recipientRole.id],
      role_name: [recipientRole?.role_name ?? null],
      recipient_id: [recipientRole.recipient_id],
      organization_id: [recipientRole.organization_id],
      personfields: this.formBuilder.array(this.helperService.getPersonfields(this.updateNameFields(recipientRole.personfields), setRecipientData(this.updateNameFields(recipientRole.personfields)), true, true)),
      orgfields: this.formBuilder.array(this.helperService.getOrgfields(recipientRole.orgfields, setOrgnizationData(recipientRole.orgfields), false, true)),
      recipient: {...setRecipientData(recipientRole.personfields), custom_fields: recipientRole.recipient?.custom_fields},
      organization: {...setOrgnizationData(recipientRole.orgfields), custom_fields: recipientRole.organization?.custom_fields},
    });
    this.roleForm.get('personfields').valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((personfields) => {
      const recipient = setRecipientData(personfields);
      this.roleForm.get('recipient').patchValue(recipient);
    });
    this.roleForm.get('orgfields').valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((orgfields) => {
      const organization = setOrgnizationData(orgfields);
      this.roleForm.get('organization').setValue(organization);
    });
    this.focusInput();
  }

  /**
     * Contact form
     * @returns response
     */
  createContact() {
    this.activity['submitted'] = true;
    if (this.roleForm.invalid) {
      return;
    }
    this.activity['loading'] = true;
    this.handleRecipientUpdate();
  }

  private handleRecipientUpdate(): void {
    const recipientId = this.roleForm.get('recipient_id')?.value;
    const recipientData = recipientId
      ? { id: recipientId, ...this.roleForm.get('recipient')?.value }
      : this.roleForm.get('recipient').value;
    if (recipientData?.phone_no && recipientData?.phone_no?.e164Number) {
      recipientData.phone_no = recipientData?.phone_no?.e164Number;
    }

    this.roleForm.get('personfields').value.forEach((item, itemIndex) => {
      if (item?.key === "phone_no" && item?.value && item?.value?.e164Number) {
        const control = this.roleForm.get(`personfields.${itemIndex}.value`);
        if (control) {
          control.patchValue(item.value.e164Number);
        }
      }
    });
    if (this.editParticipientFromSigningPage) {
      this.activity['loading'] = false;
      this.activity['submitted'] = false;
      this.activeModal.close({ recipient: recipientData, personfields: this.roleForm.get('personfields').getRawValue(), orgfields: this.roleForm.get('orgfields').getRawValue(), organization: this.roleForm.get('organization').getRawValue() });
      return;
    }

    const recipientCustomFields = this.getCustomFieldsData('personfields');
    const recipientUpdatedData = this.globalService.contactFormData({...recipientData, custom_fields: recipientCustomFields});
    
    let serviceCall;
    if(recipientId){
      serviceCall = this.recipientsService.updateRecipientForm(recipientId, recipientUpdatedData)
    } else {
      serviceCall = this.recipientsService.addRecipient(recipientUpdatedData);
    }

    serviceCall.subscribe({
      next: (recipient: Recipient) => {
        this.handleOrganizationUpdate().subscribe({
          next: (organization: IOrganizationDTO) => {
            this.activity['loading'] = false;
            this.activity['submitted'] = false;
            const recipientData = Object.assign(recipient, { organization_id: (organization?.id ?? null), organization: this.skipOrganizationFields(organization) });
            this.activeModal.close({ recipient: recipientData, personfields: this.roleForm.get('personfields').value, orgfields: this.roleForm.get('orgfields').value });
          },
          error: (err) => {
            this.globalService.toastrError(err);
          },
        });
      },
      error: (err) => {
        this.globalService.toastrError(err);
        this.activity['loading'] = false;
      },
    });
  }

  getCustomFieldsData(section){
    const allFields = this.roleForm.get(section)?.value;
    const customFields = allFields.filter(f => f.metadata_id && f.metadata_id !== 0 && f.is_enable && f.metadata_type === 'text');
    const customFieldsData = [];
    customFields.forEach(field => {
      const customField = {meta_data_id: field.metadata_id, value: field.value, field_type: 'text'};   
      customFieldsData.push(customField);   
    });
    Object.entries(this.contactAttachments[section]).forEach(([meta_data_id, file]) => {
      const customField = {meta_data_id: meta_data_id, value: file, field_type: 'image'};   
      customFieldsData.push(customField);   
    });
    return customFieldsData;
  }  

  onFileChange(event: Event, metaDataId: number, section: string, fieldIndex: number) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const allAttachments = {...this.contactAttachments};
      allAttachments[section][metaDataId] = file;
      this.contactAttachments = {...allAttachments};
      const formArray = this.roleForm.get(section) as FormArray;
      if (formArray && formArray.length > fieldIndex) {
        const fieldGroup = formArray.at(fieldIndex) as FormGroup;
        fieldGroup.patchValue({ value: file?.name });
      }
    }
  }

  private skipOrganizationFields(organization) {
    if (organization) {
      // organization is sealed
      const cloneOrganization = { ...organization };
      delete cloneOrganization['created_at'];
      delete cloneOrganization['updated_at'];
      return cloneOrganization;
    }
    return organization;
  }

  private handleOrganizationUpdate(): Observable<IOrganizationDTO | null> {
    const orgId = this.roleForm.get('organization_id')?.value;
    const orgData = this.roleForm.get('organization')?.value;
    const orgCustomFields = this.getCustomFieldsData('orgfields');
    const orgUpdatedData = {...orgData, custom_fields: orgCustomFields};
    const orgDataForm = this.globalService.contactFormData(orgUpdatedData);
    
    if (orgId) {
      return this.organizationService.updateOrganization(orgDataForm, orgId);
    } else if (orgUpdatedData?.name) {
      return this.organizationService.addOrganization(orgDataForm);
    }

    return of(null);
  }

  /**
     * Organization custom filter
     * @param items 
     * @param query 
     * @returns 
     */
  organizationCustomFilter(items: Organization[], query: string) {
    return searchOrganizationCustomFilter(items, query)
  }

  /**
     * Select organization from autocomplete
     * @param org 
     * @returns 
     */
  selectOrg(org) {
    if (org.id == 0) {
      return;
    }
    let orgfields = [];
    this.timeOutSubscription = of(null).
      pipe(
        delay(0),
        take(1)
      ).subscribe(() => {
        const orgFields = organizationFields(org, this.globalService);
        this.orgfields.value.forEach(orgfield => {
          if(orgfield?.metadata_id && org?.custom_fields?.length > 0) {
            const found = org.custom_fields.find(f => f.meta_data_id === orgfield.metadata_id);
            if (found) {
              orgfield = { ...orgfield, value: this.globalService.getContactCustomFieldValue(found) };
            }
          } else {
            const found = orgFields.find((v) => v.name == orgfield.name);
            if (found) {
                orgfield = { ...orgfield, value: found.value };
            }
          }
          orgfields.push(orgfield);
        });
        const populateOrg = new FormArray(this.helperService.getOrgfields(orgfields, org, true, true));
        orgfields = populateOrg.value;
        this.orgfields.reset(orgfields);
        this.roleForm.patchValue({ organization_id: org.id, organization: org });
      })
    this.organizationSearch = [];
  }



  /**
   * On organization search
   * @param val
   * @param field
   * @returns
   */
  async onOrgSearch(val: string) {
    if (val == '' || this.roleForm.get('organization_id').value != null) {
      this.organizationSearch = [];
      return;
    }
    this.autoCompleteLoaders['orgloader'] = true;
    this.recipientfieldsService.searchOrganization(0, val).subscribe((organization) => {
      this.organizationSearch = organization.data || [];
      this.autoCompleteLoaders['orgloader'] = false;
    });
  }



  /**
   * Clear organization
   */
  clearOrganization() {
    this.organizationSearch = [];
    const orgfields = [];
    this.orgfields.controls.forEach((element) => {
      orgfields.push({
        ...element.value,
        value: '',
      })
    });
    this.orgfields.reset(orgfields);
    this.roleForm.patchValue({ organization_id: null, organization: null });
  }

  /** Get form controls */
  get form() {
    return this.contactForm.controls;
  }

  /**
   * Get person fields
   * @access
   */
  get personfields() {
    return this.roleForm.controls["personfields"] as FormArray;
  }



  /**
   * Get organizations fields
   * @access
   */
  get orgfields() {
    return this.roleForm.controls["orgfields"] as FormArray;
  }

  get headerTitle() {
    const roleName = this.roleForm.get('role_name')?.value;

    if (this.updateRecipient) {
      return this._translocoService.translate('Contact right sidebar title (Contact - section) - Update contact');
    }

    if (this.isSendForSignature && roleName) {
      return this._translocoService.translate('Modal header text for send for signature (Online contract) Add rolename', {
        rolename: roleName,
      });
    }

    return this._translocoService.translate('Contact right sidebar title (Contact - section) - Add New Contact');
  }

  /**
   * 
   * Show hidden fields
   */
  showHiddenFields() {
    this.hiddenFields = !this.hiddenFields;
    this.changeText = this.hiddenFields ? this._translocoService.translate("Hide (general) - Hide") : this._translocoService.translate("Show more (general) - Show more")
  }

  focusInput(): void {
    this.timeoutId = setTimeout(() => {
      const inputToFocus = this.personinput.toArray()[this.activeIndex];
      if (inputToFocus) {
        try {
          const { searchInput, formControl } = inputToFocus as { searchInput?: ElementRef, formControl?: ElementRef, nativeElement: HTMLElement };

          searchInput?.nativeElement?.focus() ||
            formControl?.nativeElement?.childNodes?.[0]?.childNodes?.[2]?.focus() ||
            inputToFocus?.nativeElement?.focus();
        } catch (error) {
          console.warn('Error focusing input:', error);
        }
      }
      if (this.editParticipientFromSigningPage) {
        this.roleForm.get('personfields').value.forEach((item, itemIndex) => {
          const control = this.roleForm.get(`personfields.${itemIndex}.value`);
          if (control && item?.lock) {
            control.disable();
          }
        });
        this.roleForm.get('orgfields').value.forEach((item, itemIndex) => {
          const control = this.roleForm.get(`orgfields.${itemIndex}.value`);
          if (control && item?.lock) {
            control.disable();
          }
        });
      }
    }, 500);
  }

  updateNameFields(personField: Personfield[]) {
    if(this.updateRecipient) {
      return personField;
    }
    const fullName = personField.find(field => field.key === "first_name")?.value || '';
    const { firstname, lastname } = this.parseName.transform(fullName);
    return personField.map(field => {
      if (field.key === "first_name") {
        return { ...field, value: firstname };
      }
      if (field.key === "last_name") {
        return { ...field, value: lastname };
      }
      return field;
    });
  }

  get roleId(): number {
    return this.roleForm.get('id').value;
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeoutId);
  }
}
